import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt2 } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, Store, BookOpen, DollarSign, TrendingUp, ShieldCheck, Search,
  ShoppingCart, Activity, MessageSquare, Wallet, AlertTriangle, X
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

// ---------- KPI Card ----------
function KpiCard({ label, value, sub, Icon, color, testId }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ---------- Overview Tab ----------
function OverviewTab({ stats }) {
  const { monthly = [], user_monthly = [], top_vendors = [] } = stats;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="admin-revenue-chart">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Bookings & Revenue</h3>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="admin-rev" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#admin-rev)" name="Revenue ($)" />
                <Area type="monotone" dataKey="bookings" stroke="hsl(var(--chart-2))" strokeWidth={2} fill="transparent" name="Bookings" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border p-5" data-testid="admin-signups-chart">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">New User Signups</h3>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={user_monthly} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Bar dataKey="signups" fill="hsl(var(--chart-3))" name="New users" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-5" data-testid="admin-top-vendors">
        <h3 className="font-semibold mb-3">Top Vendors by Revenue</h3>
        {top_vendors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No paid bookings yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left">
                <tr><th className="pb-2">Vendor</th><th>Email</th><th className="text-right">Bookings</th><th className="text-right pr-1">Revenue</th></tr>
              </thead>
              <tbody>
                {top_vendors.map((v, i) => (
                  <tr key={v.user_id} className="border-t border-border" data-testid="admin-top-vendor-row">
                    <td className="py-2 font-medium">{i + 1}. {v.name}</td>
                    <td className="text-muted-foreground text-xs">{v.email}</td>
                    <td className="text-right">{v.bookings}</td>
                    <td className="text-right font-semibold pr-1">{fmt2(v.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Users Tab ----------
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (q) params.set("q", q);
      if (roleFilter !== "all") params.set("role", roleFilter);
      const r = await api.get(`/admin/users?${params}`);
      setUsers(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load users"); }
    setLoading(false);
  }, [q, roleFilter, page]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };

  const ROLE_COLORS = { tourist: "bg-sky-50 text-sky-800", vendor: "bg-emerald-50 text-emerald-800", admin: "bg-rose-50 text-rose-800" };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center flex-1 rounded-xl bg-card border border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by name or email…" className="border-0 focus-visible:ring-0 bg-transparent" data-testid="admin-users-search" />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="admin-users-role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="tourist">Tourist</SelectItem>
            <SelectItem value="vendor">Vendor</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border"><th className="p-3">Name</th><th>Email</th><th>Role</th><th>Joined</th><th className="text-right pr-3">Change role</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No users found.</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-testid="admin-user-row">
                <td className="p-3 font-medium">{u.name || "—"}</td>
                <td className="text-muted-foreground">{u.email}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role] || "bg-muted text-foreground"}`}>{u.role}</span></td>
                <td className="text-muted-foreground text-xs">{u.created_at?.slice(0, 10) || "—"}</td>
                <td className="text-right pr-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                    <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`admin-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="tourist">Tourist</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 15 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} users total</span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl h-8" data-testid="admin-users-prev">Previous</Button>
            <Button variant="ghost" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl h-8" data-testid="admin-users-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Listings Tab ----------
function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (q) params.set("q", q);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/admin/listings?${params}`);
      setListings(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load listings"); }
    setLoading(false);
  }, [q, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const patch = async (listingId, update, msg) => {
    try {
      await api.patch(`/admin/listings/${listingId}`, update);
      toast.success(msg);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-listings-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center flex-1 rounded-xl bg-card border border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by title or location…" className="border-0 focus-visible:ring-0 bg-transparent" data-testid="admin-listings-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="admin-listings-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="admin-listings-table">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border"><th className="p-3">Title</th><th>Category</th><th>Status</th><th className="text-right">Bookings</th><th className="text-right">Revenue</th><th className="text-right pr-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : listings.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No listings found.</td></tr>
            ) : listings.map((l) => (
              <tr key={l.listing_id} className="border-t border-border" data-testid="admin-listing-row">
                <td className="p-3">
                  <Link to={`/listings/${l.listing_id}`} className="font-medium hover:underline line-clamp-1">{l.title}</Link>
                  <div className="text-xs text-muted-foreground">{l.location}</div>
                </td>
                <td><span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{l.category}</span></td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${l.status === "active" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>{l.status}</span>
                  {l.featured && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">Featured</span>}
                </td>
                <td className="text-right">{l.bookings_count || 0}</td>
                <td className="text-right font-medium">{fmt2(l.revenue_total || 0)}</td>
                <td className="text-right pr-3">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" className="h-7 rounded-lg text-xs" data-testid={`admin-feature-btn-${l.listing_id}`}
                      onClick={() => patch(l.listing_id, { featured: !l.featured }, l.featured ? "Unfeatured" : "Featured")}>
                      {l.featured ? "Unfeature" : "Feature"}
                    </Button>
                    <Button variant="ghost" size="sm" className={`h-7 rounded-lg text-xs ${l.status === "active" ? "text-rose-600" : "text-emerald-700"}`} data-testid={`admin-status-btn-${l.listing_id}`}
                      onClick={() => patch(l.listing_id, { status: l.status === "active" ? "archived" : "active" }, l.status === "active" ? "Archived" : "Restored")}>
                      {l.status === "active" ? "Archive" : "Restore"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 15 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} listings total</span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl h-8" data-testid="admin-listings-prev">Previous</Button>
            <Button variant="ghost" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl h-8" data-testid="admin-listings-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Bookings Tab (with force cancel) ----------
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (q) params.set("q", q);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/admin/bookings?${params}`);
      setBookings(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load bookings"); }
    setLoading(false);
  }, [q, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const forceCancel = async (bookingId, issueRefund) => {
    if (!window.confirm(`Force cancel booking ${bookingId}? ${issueRefund ? "A refund will be attempted." : "No refund."}`)) return;
    setCancelling(bookingId);
    try {
      const r = await api.post(`/admin/bookings/${bookingId}/force-cancel`, { issue_refund: issueRefund, reason: "Admin force cancel" });
      toast.success(`Cancelled. Status: ${r.data.status}${r.data.refund_amount > 0 ? `. Refund: ${fmt2(r.data.refund_amount)}` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    setCancelling(null);
  };

  const STATUS_COLORS = { pending: "bg-amber-50 text-amber-800", confirmed: "bg-sky-50 text-sky-800", paid: "bg-emerald-50 text-emerald-800", cancelled: "bg-rose-50 text-rose-800", refunded: "bg-blue-50 text-blue-800", force_cancelled: "bg-rose-100 text-rose-900" };

  return (
    <div className="space-y-4" data-testid="admin-bookings-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center flex-1 rounded-xl bg-card border border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by guest, email or trip…" className="border-0 focus-visible:ring-0 bg-transparent" data-testid="admin-bookings-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="admin-bookings-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="admin-bookings-table">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border"><th className="p-3">Trip</th><th>Guest</th><th>Date</th><th>Status</th><th className="text-right">Amount</th><th className="text-right pr-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            : bookings.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No bookings found.</td></tr>
            : bookings.map((b) => (
              <tr key={b.booking_id} className="border-t border-border" data-testid="admin-booking-row">
                <td className="p-3">
                  <Link to={`/listings/${b.listing_id}`} className="font-medium hover:underline line-clamp-1">{b.title}</Link>
                  <div className="text-xs text-muted-foreground font-mono">{b.booking_id}</div>
                </td>
                <td><div>{b.contact_name}</div><div className="text-xs text-muted-foreground">{b.contact_email}</div></td>
                <td className="text-muted-foreground">{b.date}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[b.status] || "bg-muted text-foreground"}`}>{b.status}</span></td>
                <td className="text-right font-semibold">{fmt2(b.line_final)}</td>
                <td className="text-right pr-3">
                  {!["cancelled", "refunded", "force_cancelled"].includes(b.status) && (
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 text-xs rounded-lg text-emerald-700 hover:bg-emerald-50"
                        disabled={cancelling === b.booking_id} onClick={() => forceCancel(b.booking_id, true)} data-testid={`admin-refund-btn-${b.booking_id}`}>
                        Refund
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs rounded-lg text-rose-600 hover:bg-rose-50"
                        disabled={cancelling === b.booking_id} onClick={() => forceCancel(b.booking_id, false)} data-testid={`admin-cancel-btn-${b.booking_id}`}>
                        <X className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 15 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} bookings total</span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl h-8" data-testid="admin-bookings-prev">Previous</Button>
            <Button variant="ghost" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl h-8" data-testid="admin-bookings-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Messages Tab (admin moderation) ----------
function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (q) params.set("q", q);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/messages/admin/all?${params}`);
      setMessages(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load messages"); }
    setLoading(false);
  }, [q, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (messageId, status) => {
    try {
      await api.patch(`/messages/admin/${messageId}/moderate`, { status });
      toast.success(`Message ${status}`);
      load();
    } catch (e) { toast.error("Failed to moderate message"); }
  };

  const STATUS_COLORS = { active: "bg-emerald-50 text-emerald-800", flagged: "bg-amber-50 text-amber-800", deleted: "bg-rose-50 text-rose-800" };

  return (
    <div className="space-y-4" data-testid="admin-messages-tab">
      <div className="flex gap-3 flex-col sm:flex-row">
        <div className="flex items-center flex-1 rounded-xl bg-card border border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by buyer, listing or subject…" className="border-0 focus-visible:ring-0 bg-transparent" data-testid="admin-messages-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        {loading ? Array.from({length:4}).map((_,i)=><div key={`skel-msg-${i}`} className="h-20 rounded-2xl bg-muted animate-pulse" />) :
        messages.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No messages found.</div> :
        messages.map(m => (
          <div key={m.message_id} className="rounded-2xl bg-card border border-border p-4" data-testid="admin-message-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{m.subject}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status]||"bg-muted"}`}>{m.status}</span>
                  {m.replies?.length > 0 && <span className="text-xs text-muted-foreground">{m.replies.length} replies</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  <span className="font-medium text-foreground">{m.buyer_name}</span> ({m.buyer_email}) →&nbsp;
                  <Link to={`/listings/${m.listing_id}`} className="hover:underline">{m.listing_title}</Link>
                  <span className="ml-2">{m.created_at?.slice(0,10)}</span>
                </div>
                <p className="text-sm mt-1 line-clamp-2 text-muted-foreground">{m.body}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {m.status !== "flagged" && <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:bg-amber-50 rounded-lg" onClick={() => moderate(m.message_id, "flagged")} data-testid={`admin-flag-msg-${m.message_id}`}><AlertTriangle className="h-3 w-3 mr-1"/>Flag</Button>}
                {m.status !== "deleted" && <Button variant="ghost" size="sm" className="h-7 text-xs text-rose-600 hover:bg-rose-50 rounded-lg" onClick={() => moderate(m.message_id, "deleted")} data-testid={`admin-delete-msg-${m.message_id}`}><X className="h-3 w-3 mr-1"/>Delete</Button>}
                {m.status !== "active" && <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50 rounded-lg" onClick={() => moderate(m.message_id, "active")} data-testid={`admin-approve-msg-${m.message_id}`}>Restore</Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {total > 15 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} messages</span>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="rounded-xl h-8">Previous</Button>
            <Button variant="ghost" disabled={page*15>=total} onClick={()=>setPage(p=>p+1)} className="rounded-xl h-8">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Abandoned Carts Tab ----------
function AbandonedCartsTab() {
  const [carts, setCarts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/abandoned-carts?page=${page}&page_size=15`)
      .then(r => { setCarts(r.data.items); setTotal(r.data.total); })
      .catch(() => toast.error("Failed to load abandoned carts"))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="space-y-4" data-testid="admin-abandoned-carts-tab">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ShoppingCart className="h-4 w-4" />
        Carts with items abandoned for more than 1 hour — {total} users
      </div>
      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="admin-abandoned-carts-table">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border"><th className="p-3">User</th><th>Email</th><th>Items</th><th>Oldest item</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Loading…</td></tr> :
            carts.length === 0 ? <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No abandoned carts found.</td></tr> :
            carts.map(c => (
              <tr key={c.user_id} className="border-t border-border" data-testid="admin-abandoned-cart-row">
                <td className="p-3 font-medium">{c.name || "Unknown"}</td>
                <td className="text-muted-foreground">{c.email}</td>
                <td><span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full font-medium">{c.item_count} item{c.item_count !== 1 ? "s" : ""}</span></td>
                <td className="text-xs text-muted-foreground">{c.oldest_item_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 15 && (
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="rounded-xl h-8">Previous</Button>
          <Button variant="ghost" disabled={page*15>=total} onClick={()=>setPage(p=>p+1)} className="rounded-xl h-8">Next</Button>
        </div>
      )}
    </div>
  );
}

// ---------- Activity Monitor Tab ----------
const EVENT_COLORS = {
  login: "bg-sky-100 text-sky-800", view: "bg-slate-100 text-slate-700",
  cart_add: "bg-amber-100 text-amber-800", checkout_start: "bg-purple-100 text-purple-800",
  booking: "bg-emerald-100 text-emerald-800", message: "bg-rose-100 text-rose-800",
  cancellation: "bg-red-100 text-red-800",
};

function ActivityTab() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [eventFilter, setEventFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 30 });
      if (eventFilter !== "all") params.set("event", eventFilter);
      const r = await api.get(`/admin/activity?${params}`);
      setEvents(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load activity"); }
    setLoading(false);
  }, [eventFilter, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-activity-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">{total} events tracked</div>
        <div className="flex items-center gap-2">
          <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="admin-activity-event-filter"><SelectValue placeholder="All events" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="login">Login</SelectItem>
              <SelectItem value="view">Listing view</SelectItem>
              <SelectItem value="cart_add">Cart add</SelectItem>
              <SelectItem value="checkout_start">Checkout</SelectItem>
              <SelectItem value="booking">Booking</SelectItem>
              <SelectItem value="message">Message</SelectItem>
              <SelectItem value="cancellation">Cancellation</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} className="rounded-xl h-8" data-testid="admin-activity-refresh">Refresh</Button>
        </div>
      </div>
      <div className="rounded-2xl bg-card border border-border divide-y divide-border" data-testid="admin-activity-feed">
        {loading ? Array.from({length:8}).map((_,i)=><div key={`act-skel-${i}`} className="h-12 animate-pulse bg-muted/50" />) :
        events.length === 0 ? <div className="p-8 text-center text-muted-foreground">No events yet.</div> :
        events.map(e => (
          <div key={e.activity_id} className="p-3 flex items-center gap-3" data-testid="admin-activity-event">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${EVENT_COLORS[e.event] || "bg-muted text-foreground"}`}>{e.event}</span>
            <span className="text-sm flex-1 truncate">
              <span className="font-medium">{e.user_name || e.user_email || "Anonymous"}</span>
              {e.metadata?.listing_title && <span className="text-muted-foreground"> — {e.metadata.listing_title}</span>}
              {e.metadata?.booking_group_id && <span className="text-muted-foreground font-mono text-xs"> {e.metadata.booking_group_id}</span>}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">{e.created_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>
      {total > 30 && (
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="rounded-xl h-8">Previous</Button>
          <Button variant="ghost" disabled={page*30>=total} onClick={()=>setPage(p=>p+1)} className="rounded-xl h-8">Next</Button>
        </div>
      )}
    </div>
  );
}

// ---------- Payout Requests Tab (admin) ----------
function PayoutsTab() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/admin/payout-requests?${params}`);
      setPayouts(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load payout requests"); }
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const process = async (payoutId, action) => {
    if (!window.confirm(`${action.replace("_"," ")} payout ${payoutId}?`)) return;
    setProcessing(payoutId);
    try {
      await api.patch(`/admin/payout-requests/${payoutId}`, { action });
      toast.success(`Payout ${action.replace("_"," ")} successfully`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setProcessing(null);
  };

  const STATUS_COLORS = { pending: "bg-amber-50 text-amber-800", approved: "bg-sky-50 text-sky-800", rejected: "bg-rose-50 text-rose-800", paid: "bg-emerald-50 text-emerald-800" };

  return (
    <div className="space-y-4" data-testid="admin-payouts-tab">
      <div className="flex gap-3 justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} payout requests</span>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="admin-payouts-table">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border"><th className="p-3">Vendor</th><th>Bookings</th><th>Net amount</th><th>Submitted</th><th>Status</th><th className="text-right pr-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr> :
            payouts.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No payout requests.</td></tr> :
            payouts.map(p => (
              <tr key={p.payout_id} className="border-t border-border" data-testid="admin-payout-row">
                <td className="p-3">
                  <div className="font-medium">{p.vendor_name}</div>
                  <div className="text-xs text-muted-foreground">{p.vendor_email}</div>
                </td>
                <td>{p.booking_count}</td>
                <td className="font-semibold">{fmt2(p.amount_net)}</td>
                <td className="text-muted-foreground text-xs">{p.created_at?.slice(0, 10)}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[p.status] || "bg-muted"}`}>{p.status}</span></td>
                <td className="text-right pr-3">
                  {p.status === "pending" && (
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50 rounded-lg" disabled={processing===p.payout_id} onClick={() => process(p.payout_id, "approve")} data-testid={`admin-approve-payout-${p.payout_id}`}>Approve</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-rose-600 hover:bg-rose-50 rounded-lg" disabled={processing===p.payout_id} onClick={() => process(p.payout_id, "reject")} data-testid={`admin-reject-payout-${p.payout_id}`}>Reject</Button>
                    </div>
                  )}
                  {p.status === "approved" && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-700 hover:bg-purple-50 rounded-lg" disabled={processing===p.payout_id} onClick={() => process(p.payout_id, "mark_paid")} data-testid={`admin-markpaid-payout-${p.payout_id}`}>Mark Paid</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 15 && (
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="rounded-xl h-8">Previous</Button>
          <Button variant="ghost" disabled={page*15>=total} onClick={()=>setPage(p=>p+1)} className="rounded-xl h-8">Next</Button>
        </div>
      )}
    </div>
  );
}

// ---------- Main Admin Dashboard ----------
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => toast.error("Failed to load admin stats"));
  }, []);

  const kpis = stats ? [
    { label: "Total Users",       value: stats.users.total,         sub: `${stats.users.tourists} travelers · ${stats.users.vendors} vendors`, Icon: Users,     color: "bg-sky-100 text-sky-700",     testId: "kpi-total-users" },
    { label: "Platform Revenue",  value: fmt2(stats.revenue),       sub: `${stats.bookings.paid} paid bookings`,                                Icon: DollarSign, color: "bg-emerald-100 text-emerald-700", testId: "kpi-revenue" },
    { label: "Total Bookings",    value: stats.bookings.total,      sub: `${stats.bookings.cancelled} cancelled/refunded`,                      Icon: BookOpen,   color: "bg-amber-100 text-amber-700", testId: "kpi-bookings" },
    { label: "Active Listings",   value: stats.listings.active,     sub: `${stats.listings.total} total`,                                       Icon: Store,      color: "bg-rose-100 text-rose-700",   testId: "kpi-listings" },
  ] : [];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--vermilion))]" />
            <span className="text-xs font-semibold text-[hsl(var(--vermilion))] uppercase tracking-wider">Admin</span>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Platform Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Real-time overview of IndoVoyage.</p>
        </div>
        <Link to="/"><Button variant="secondary" className="rounded-xl">Back to site</Button></Link>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats ? kpis.map((k) => <KpiCard key={k.testId} {...k} />) : (
          Array.from({ length: 4 }).map((_, i) => <div key={`kpi-skel-${i}`} className="rounded-2xl bg-muted h-28 animate-pulse" />)
        )}
      </div>

      {/* Tabbed detail sections */}
      <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
        <TabsList className="bg-muted mb-5 flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" data-testid="admin-tab-overview"><TrendingUp className="h-4 w-4 mr-2" />Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-2" />Users</TabsTrigger>
          <TabsTrigger value="listings" data-testid="admin-tab-listings"><Store className="h-4 w-4 mr-2" />Listings</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings"><BookOpen className="h-4 w-4 mr-2" />Bookings</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages"><MessageSquare className="h-4 w-4 mr-2" />Messages</TabsTrigger>
          <TabsTrigger value="carts" data-testid="admin-tab-carts"><ShoppingCart className="h-4 w-4 mr-2" />Abandoned Carts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity"><Activity className="h-4 w-4 mr-2" />Activity</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts"><Wallet className="h-4 w-4 mr-2" />Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {stats ? <OverviewTab stats={stats} /> : <div className="rounded-2xl bg-muted h-64 animate-pulse" />}
        </TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="listings"><ListingsTab /></TabsContent>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="carts"><AbandonedCartsTab /></TabsContent>
        <TabsContent value="activity"><ActivityTab /></TabsContent>
        <TabsContent value="payouts"><PayoutsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
